import ErrorHandler from "../error-handlers/axios/defaultErrorHandler";

/**
 * @description Get all sections (no pagination)
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<Array>} Array of sections
 */
export const getAllSections = async (axiosInstance, options) => {
  try {
    const res = await axiosInstance.get(`/sections/all`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Get sections with pagination and search
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {object} params - Query parameters
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Paginated sections response
 */
export const getSections = async (axiosInstance, params = {}, options) => {
  try {
    const res = await axiosInstance.get(`/sections`, { 
      ...options,
      params: { ...options?.params, ...params }
    });
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Get a single section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Section data
 */
export const getSection = async (axiosInstance, sectionId, options) => {
  try {
    const res = await axiosInstance.get(`/sections/${sectionId}`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Get section with its cards
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {object} params - Query parameters for cards
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Section with cards
 */
export const getSectionWithCards = async (axiosInstance, sectionId, params = {}, options) => {
  try {
    const res = await axiosInstance.get(`/sections/${sectionId}/with-cards`, {
      ...options,
      params: { ...options?.params, ...params }
    });
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Create a new section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {object} payload - Section data
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Created section
 */
export const createSection = async (axiosInstance, payload, options) => {
  try {
    const res = await axiosInstance.post(`/sections`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Update a section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {object} payload - Updated section data
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Updated section
 */
export const updateSection = async (axiosInstance, sectionId, payload, options) => {
  try {
    const res = await axiosInstance.patch(`/sections/${sectionId}`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Delete a section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<void>}
 */
export const deleteSection = async (axiosInstance, sectionId, options) => {
  try {
    await axiosInstance.delete(`/sections/${sectionId}`, options);
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
}; 